// Built-ins
import React, { useState, useEffect } from "react";

// Icons / Images
import { BiPlus } from "react-icons/bi";
import { AiOutlineWarning, AiOutlineCheckCircle } from "react-icons/ai";
import axios from "axios";

// Modules / Functions
import Swal from "sweetalert2";
import { Modal, Form, Row, Col } from "react-bootstrap";
import GuardedModal from "../../common/GuardedModal";
import { Button } from "semantic-ui-react";

// Global Variables
import { headers } from "../../global_vars";
import SelectWithInlineCreate from "../../common/SelectWithInlineCreate";
import RichTextEditor from "../../common/RichTextEditor";
import { htmlForApi, plainTextLength } from "../../../utils/richText";

// Variables
window.Swal = Swal;

const CREATE_TASK_COMMENT = "https://ultima.icsgr.com/api/data_management/task_comments/";

function AddTaskCommentModal({ onTaskCommentCreated, refreshData, defaultProjectTaskId, lockProjectTask = false }) {
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [projectTasks, setProjectTasks] = useState([]);
  const [authorLabel, setAuthorLabel] = useState(localStorage.getItem("username") || "");

  // Form state
  const [projtask, setProjtask] = useState("");
  const [comment, setComment] = useState("");
  const authorId = localStorage.getItem("consultant_id") || "";

  // Fetch data when modal opens
  useEffect(() => {
    if (show) {
      fetchProjectTasks();
      fetchConsultants();
    }
  }, [show]);

  const fetchProjectTasks = async () => {
    try {
      const currentHeaders = {
        ...headers,
        "Authorization": "Token " + localStorage.getItem("userToken"),
      };
      const res = await axios.get(
        "https://ultima.icsgr.com/api/data_management/project_tasks/",
        { headers: currentHeaders }
      );
      let tasks = [];
      if (Array.isArray(res.data)) tasks = res.data;
      else if (Array.isArray(res.data?.results)) tasks = res.data.results;
      else if (Array.isArray(res.data?.data)) tasks = res.data.data;
      else tasks = res.data?.all_project_tasks || [];
      setProjectTasks(tasks);
    } catch (error) {
      console.error("Error fetching project tasks:", error);
      setProjectTasks([]);
    }
  };

  const fetchConsultants = async () => {
    try {
      const currentHeaders = {
        ...headers,
        "Authorization": "Token " + localStorage.getItem("userToken"),
      };
      const res = await axios.get(
        "https://ultima.icsgr.com/api/administration/all_consultants/",
        { headers: currentHeaders }
      );
      let list = [];
      if (Array.isArray(res.data)) list = res.data;
      else if (Array.isArray(res.data?.results)) list = res.data.results;
      else if (Array.isArray(res.data?.data)) list = res.data.data;
      else list = res.data?.all_consultants || [];
      const me = list.find((c) => String(c.consultant_id) === String(authorId));
      if (me) {
        setAuthorLabel(me.fullname || me.username || authorId);
      }
    } catch (error) {
      console.error("Error fetching consultants:", error);
    }
  };

  // Validation
  const isProjtaskValid = projtask !== "";
  const isAuthorValid = authorId !== "";
  const isCommentValid = plainTextLength(comment) >= 2 && plainTextLength(comment) <= 1000;

  const isFormValid = isProjtaskValid && isAuthorValid && isCommentValid;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isFormValid) {
      return;
    }

    setLoading(true);

    try {
      const taskCommentData = {
        projtask_id: projtask,
        consultant_id: authorId,
        comment: htmlForApi(comment),
      };

      const currentHeaders = {
        ...headers,
        "Authorization": "Token " + localStorage.getItem("userToken")
      };

      const res = await axios.post(
        CREATE_TASK_COMMENT,
        taskCommentData,
        { headers: currentHeaders }
      );

      Swal.fire({ icon: "success", title: "Success!", text: "Task Comment created successfully" });

      if (typeof refreshData === "function") {
        handleClose();
        try { await refreshData(); } catch (_) {}
      } else if (typeof onTaskCommentCreated === "function") {
        handleClose();
        try { await onTaskCommentCreated(res.data); } catch (_) {}
      } else {
        const newId = res?.data?.taskcomm_id || res?.data?.id;
        if (newId) window.location.href = `/data_management/task_comment/${newId}`;
        else handleClose();
      }
    } catch (error) {
      console.error("Error creating task comment:", error);
      
      let errorMessage = "Failed to create task comment";
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      }

      Swal.fire({
        icon: "error",
        title: "Error",
        text: errorMessage,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setShow(false);
    setProjtask("");
    setComment("");
  };

  return (
    <>
      <Button
        color="green"
        style={{ margin: 20 }}
        onClick={() => { if (defaultProjectTaskId) setProjtask(defaultProjectTaskId); setShow(true); }}
      >
        <BiPlus
          style={{ color: "white", fontSize: "1.3em", marginRight: "0.3em" }}
        />
        Create Task Comment
      </Button>

      <GuardedModal show={show} onHide={handleClose} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Create New Task Comment</Modal.Title>
        </Modal.Header>
        <form onSubmit={handleSubmit}>
          <Modal.Body>
            <Row>
              <Col md={6}>
                <SelectWithInlineCreate
                  className="mb-3"
                  label="Project Task *"
                  required
                  value={projtask}
                  onChange={setProjtask}
                  disabled={lockProjectTask}
                  placeholderOption="Select a project task"
                  overviewLinkEntity="project_task"
                >
                  {projectTasks.map((task) => (
                    <option key={task.projtask_id} value={task.projtask_id} title={task.projtask_id}>
                      {task.title}
                    </option>
                  ))}
                </SelectWithInlineCreate>
              </Col>
            </Row>

            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>By</Form.Label>
                  <Form.Control plaintext readOnly value={authorLabel || authorId || "Logged-in user"} />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Comment *</Form.Label>
                  <RichTextEditor
                    rows={4}
                    value={comment}
                    onChange={setComment}
                    maxPlainLength={1000}
                    placeholder="Enter your comment"
                    isInvalid={!isCommentValid}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <small className="mr-auto">
              {!isFormValid ? (
                <ul
                  className="mr-auto"
                  style={{ margin: 0, padding: 0, color: "red" }}
                >
                  {!isProjtaskValid && (
                    <li>
                      <AiOutlineWarning style={{ fontSize: 18, marginRight: 6 }} />
                      Project Task is required.
                    </li>
                  )}
                  {!isAuthorValid && (
                    <li>
                      <AiOutlineWarning style={{ fontSize: 18, marginRight: 6 }} />
                      Log in again so the comment is saved under your name.
                    </li>
                  )}
                  {!isCommentValid && (
                    <li>
                      <AiOutlineWarning style={{ fontSize: 18, marginRight: 6 }} />
                      Comment is required (2–1000 chars).
                    </li>
                  )}
                </ul>
              ) : (
                <ul
                  className="mr-auto"
                  style={{ margin: 0, padding: 0, color: "green" }}
                >
                  <li>
                    <AiOutlineCheckCircle
                      style={{ fontSize: 18, marginRight: 6 }}
                    />
                    Validated
                  </li>
                </ul>
              )}
            </small>

            <Button color="red" onClick={handleClose} disabled={loading}>
              Close
            </Button>
            <Button
              color="green"
              onClick={handleSubmit}
              disabled={!isFormValid || loading}
            >
              {loading ? "Creating..." : "Save Changes"}
            </Button>
          </Modal.Footer>
        </form>
      </GuardedModal>
    </>
  );
}

export default AddTaskCommentModal;
